import React, { useState } from "react";
import { collection, doc, setDoc, updateDoc, writeBatch } from "firebase/firestore";
import { db } from "../firebase";
import { DEFAULT_SEASON_RULES, Season } from "../types";
import { useClubSeasons } from "../useClubSeasons";
import { useLeagueStandings } from "../useLeagueStandings";
import { LeagueTable, LeagueRulesCard } from "./LeagueTable";
import VenuePicker from "./VenuePicker";

// ── Liigan kaudet (asetukset) ─────────────────────────────────
//
// Kausi luodaan yhdellä lomakkeella, josta syntyy koko kauden tapahtumat
// kerralla: viikonpäivä + kellonaika + ensimmäinen päivä + lukumäärä.
// Toistuvuutta ei mallinneta datassa — generaattori kirjoittaa tavallisia
// tapahtumia, joita voi muokata ja poistaa yksitellen. Ks. docs/liiga.md.

const C = {
  card: "var(--sl-card)",
  navy: "var(--sl-primary)", gold: "var(--sl-accent)", text: "var(--sl-text)", muted: "var(--sl-muted)", white: "#ffffff",
  border: "var(--sl-border)", soft: "var(--sl-soft)", error: "var(--sl-error)", accent: "var(--sl-success)",
};
const F = { heading: "'Rajdhani', sans-serif", body: "'Sora', sans-serif" };
const card: React.CSSProperties = {
  background: C.card, border: `1px solid ${C.border}`,
  borderRadius: 16, padding: 18, marginBottom: 14,
  boxShadow: "0 4px 16px rgba(22,59,104,0.06)",
};
const btn: React.CSSProperties = {
  padding: "10px 18px", borderRadius: 10, border: "none",
  cursor: "pointer", fontWeight: 700, fontSize: 13, fontFamily: F.body,
};
const input: React.CSSProperties = {
  width: "100%", padding: "12px 14px", boxSizing: "border-box",
  background: C.card, border: `1.5px solid ${C.border}`,
  borderRadius: 10, fontSize: 14, color: C.text,
  fontFamily: F.body, outline: "none", marginBottom: 10,
};
const label: React.CSSProperties = { fontSize: 12, color: C.muted, marginBottom: 4 };

const WEEKDAYS_FI = ["Sunnuntai", "Maanantai", "Tiistai", "Keskiviikko", "Torstai", "Perjantai", "Lauantai"];
// "lauantaisin", ei "lauantaiisin": toistuvuuden muoto ei synny perusmuodosta.
const WEEKDAYS_FI_EVERY = ["sunnuntaisin", "maanantaisin", "tiistaisin", "keskiviikkoisin", "torstaisin", "perjantaisin", "lauantaisin"];
const WEEKDAYS_EN = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

/** Nelinumeroinen PIN, sama muoto kuin AdminView.genPin. */
function genPin(): string {
  return String(Math.floor(1000 + Math.random() * 9000));
}

/** Paikallinen päivä + kellonaika ISO-merkkijonoksi. */
function localIso(date: string, time: string): string {
  return new Date(`${date}T${time}`).toISOString();
}

/** Ensimmäinen annettu viikonpäivä, joka on >= päivä. */
function firstWeekdayFrom(date: string, weekday: number): Date {
  const d = new Date(`${date}T12:00`);
  while (d.getDay() !== weekday) d.setDate(d.getDate() + 1);
  return d;
}

function ymd(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

interface Props {
  clubId: string;
  lang?: "fi" | "en";
  /** Kutsutaan kun kausi on luotu, jotta tapahtumalista päivittyy. */
  onChanged?: () => void;
}

/** Kauden taulukko ylläpitäjälle, avataan riviltä. */
const SeasonStandings: React.FC<{ clubId: string; season: Season; lang: "fi" | "en" }> = ({ clubId, season, lang }) => {
  const { standings, loading, photos } = useLeagueStandings(clubId, season);
  return (
    <div style={{ marginTop: 8 }}>
      <LeagueTable season={season} standings={standings} loading={loading} photos={photos} lang={lang} />
      <LeagueRulesCard season={season} standings={standings} loading={loading} lang={lang} />
    </div>
  );
};

const SeasonSettings: React.FC<Props> = ({ clubId, lang = "fi", onChanged }) => {
  const [openTable, setOpenTable] = useState<string | null>(null);
  const { seasons, loaded, reload } = useClubSeasons(clubId);
  const [creating, setCreating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [name, setName] = useState("");
  const [weekday, setWeekday] = useState(6); // lauantai
  const [time, setTime] = useState("10:00");
  const [firstDate, setFirstDate] = useState(() => ymd(new Date()));
  const [count, setCount] = useState("12");
  const [venue, setVenue] = useState("");
  const [venueAddress, setVenueAddress] = useState("");
  const [panos, setPanos] = useState("0");
  const [countBest, setCountBest] = useState(String(DEFAULT_SEASON_RULES.countBest));
  const [minEvents, setMinEvents] = useState(String(DEFAULT_SEASON_RULES.minEvents));

  const days = lang === "fi" ? WEEKDAYS_FI : WEEKDAYS_EN;
  const n = Math.max(1, Math.min(30, Number(count) || 0));
  const first = firstWeekdayFrom(firstDate, weekday);
  const last = new Date(first); last.setDate(last.getDate() + 7 * (n - 1));

  async function create() {
    const nm = name.trim();
    if (!nm) { setError(lang === "fi" ? "Anna kaudelle nimi." : "Give the season a name."); return; }
    setSaving(true);
    setError(null);
    try {
      const rules = {
        ...DEFAULT_SEASON_RULES,
        countBest: Math.max(1, Number(countBest) || DEFAULT_SEASON_RULES.countBest),
        minEvents: Math.max(0, Number(minEvents) || 0),
      };
      const seasonRef = doc(collection(db, "clubs", clubId, "seasons"));
      await setDoc(seasonRef, {
        name: nm,
        startsAt: localIso(ymd(first), time),
        endsAt: localIso(ymd(last), time),
        status: "open",
        rules,
        createdAt: new Date().toISOString(),
      });

      // Tapahtumat yhdessä erässä: joko kaikki syntyvät tai ei mikään.
      const batch = writeBatch(db);
      for (let i = 0; i < n; i++) {
        const d = new Date(first); d.setDate(d.getDate() + 7 * i);
        const ref = doc(collection(db, "clubs", clubId, "events"));
        batch.set(ref, {
          sport: "pickleball",
          venue: venue.trim(), venueAddress: venueAddress.trim(),
          panos: Number(panos) || 0,
          datetime: localIso(ymd(d), time),
          pin: genPin(), gameType: "Americano", status: "open",
          seasonId: seasonRef.id,
          createdAt: new Date().toISOString(),
        });
      }
      await batch.commit();

      setCreating(false);
      setName("");
      reload();
      onChanged?.();
    } catch (err) {
      console.error("season create", err);
      setError(lang === "fi"
        ? "Kauden luonti epäonnistui. Tarkista yhteys ja yritä uudelleen."
        : "Could not create the season. Check your connection and try again.");
    } finally {
      setSaving(false);
    }
  }

  async function finish(s: Season) {
    const ok = window.confirm(lang === "fi"
      ? `Päätetäänkö kausi ${s.name}? Taulukko jää luettavaksi, mutta uusia tapahtumia ei enää liitetä siihen.`
      : `End season ${s.name}? The table stays readable, but no new events are added to it.`);
    if (!ok) return;
    try {
      await updateDoc(doc(db, "clubs", clubId, "seasons", s.id), { status: "finished" });
      reload();
      onChanged?.();
    } catch (err) {
      console.error("season finish", err);
    }
  }

  const fmt = (iso: string) => new Date(iso).toLocaleDateString("fi-FI", { day: "numeric", month: "numeric", year: "numeric" });

  return (
    <div style={{ marginBottom: 24 }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 16 }}>
        🏆 {lang === "fi" ? "Liiga" : "League"}
      </div>

      <div style={card}>
        <div style={{ fontSize: 11, color: C.muted, lineHeight: 1.5, marginBottom: 12 }}>
          {lang === "fi"
            ? "Kausi kokoaa viikoittaiset Americano-illat yhdeksi sarjaksi. Illan sijoitus muuttuu kauden pisteiksi, ja kaudesta lasketaan parhaat illat — poissaolo ei pudota pelistä."
            : "A season groups weekly Americano nights into one series. Each night's placement becomes season points, and only the best nights count — missing a night does not drop you out."}
        </div>

        {loaded && seasons.length === 0 && !creating && (
          <div style={{ fontSize: 13, color: C.muted, marginBottom: 12 }}>
            {lang === "fi" ? "Ei vielä kausia." : "No seasons yet."}
          </div>
        )}

        {seasons.map(s => (
          <div key={s.id} style={{
            display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10,
            padding: "10px 0", borderBottom: `1px solid ${C.border}`,
          }}>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: C.text, fontFamily: F.heading }}>
                {s.name}
                {s.status === "finished" && (
                  <span style={{ fontSize: 10, color: C.muted, marginLeft: 8, fontWeight: 600 }}>
                    {lang === "fi" ? "PÄÄTTYNYT" : "ENDED"}
                  </span>
                )}
              </div>
              <div style={{ fontSize: 12, color: C.muted, marginTop: 2 }}>
                {fmt(s.startsAt)} – {fmt(s.endsAt)} · {lang === "fi"
                  ? `${s.rules.countBest} parasta lasketaan, vähintään ${s.rules.minEvents} iltaa`
                  : `best ${s.rules.countBest} count, min ${s.rules.minEvents} nights`}
              </div>
            </div>
            <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
              <button onClick={() => setOpenTable(openTable === s.id ? null : s.id)}
                style={{ ...btn, background: C.navy, color: C.white, padding: "8px 12px", whiteSpace: "nowrap" }}>
                {openTable === s.id ? (lang === "fi" ? "Sulje" : "Close") : (lang === "fi" ? "Taulukko" : "Table")}
              </button>
              {s.status === "open" && (
                <button onClick={() => finish(s)}
                  style={{ ...btn, background: C.soft, color: C.text, padding: "8px 12px", whiteSpace: "nowrap" }}>
                  {lang === "fi" ? "Päätä" : "End"}
                </button>
              )}
            </div>
          </div>
        ))}
        {openTable && seasons.some(s => s.id === openTable) && (
          <SeasonStandings clubId={clubId} season={seasons.find(s => s.id === openTable)!} lang={lang} />
        )}

        {creating ? (
          <div style={{ marginTop: 12 }}>
            <input value={name} onChange={e => setName(e.target.value)} autoFocus
              placeholder={lang === "fi" ? "Kauden nimi, esim. Talviliiga 2026–27" : "Season name, e.g. Winter League 2026–27"} style={input} />

            <div style={{ display: "flex", gap: 8 }}>
              <div style={{ flex: 1 }}>
                <div style={label}>{lang === "fi" ? "Viikonpäivä" : "Weekday"}</div>
                <select value={weekday} onChange={e => setWeekday(Number(e.target.value))}
                  style={{ ...input, appearance: "auto" as React.CSSProperties["appearance"] }}>
                  {[1, 2, 3, 4, 5, 6, 0].map(d => <option key={d} value={d}>{days[d]}</option>)}
                </select>
              </div>
              <div style={{ flex: 1 }}>
                <div style={label}>{lang === "fi" ? "Kellonaika" : "Time"}</div>
                <input value={time} onChange={e => setTime(e.target.value)} type="time" style={input} />
              </div>
            </div>

            <div style={{ display: "flex", gap: 8 }}>
              <div style={{ flex: 1 }}>
                <div style={label}>{lang === "fi" ? "Alkaen (viikko)" : "Starting (week of)"}</div>
                <input value={firstDate} onChange={e => setFirstDate(e.target.value)} type="date" style={input} />
              </div>
              <div style={{ flex: 1 }}>
                <div style={label}>{lang === "fi" ? "Iltoja" : "Nights"}</div>
                <input value={count} onChange={e => setCount(e.target.value)} type="number" min="1" max="30" style={input} />
              </div>
            </div>

            <div style={label}>{lang === "fi" ? "Pelipaikka" : "Venue"}</div>
            <VenuePicker clubId={clubId} lang={lang} inputStyle={input}
              value={{ venue, address: venueAddress }}
              onChange={v => { setVenue(v.venue); setVenueAddress(v.address); }} />

            <div style={label}>{lang === "fi" ? "Panos per ilta (€) — 0 jos ilmainen" : "Entry fee per night (€) — 0 if free"}</div>
            <input value={panos} onChange={e => setPanos(e.target.value)} type="number" min="0" style={input} />

            <div style={{ display: "flex", gap: 8 }}>
              <div style={{ flex: 1 }}>
                <div style={label}>{lang === "fi" ? "Parhaat illat lasketaan" : "Best nights counted"}</div>
                <input value={countBest} onChange={e => setCountBest(e.target.value)} type="number" min="1" style={input} />
              </div>
              <div style={{ flex: 1 }}>
                <div style={label}>{lang === "fi" ? "Vähintään iltoja palkintoon" : "Min nights for prizes"}</div>
                <input value={minEvents} onChange={e => setMinEvents(e.target.value)} type="number" min="0" style={input} />
              </div>
            </div>

            <div style={{ fontSize: 12, color: C.navy, background: `var(--sl-primary-a06)`, border: `1px solid var(--sl-primary-a20)`, borderRadius: 10, padding: "10px 12px", marginBottom: 10, lineHeight: 1.5 }}>
              {lang === "fi"
                ? <>Luodaan <strong>{n}</strong> tapahtumaa: {WEEKDAYS_FI_EVERY[weekday]} klo {time}, {fmt(first.toISOString())} – {fmt(last.toISOString())}. Jokaista voi muokata tai poistaa erikseen.</>
                : <>Creates <strong>{n}</strong> events: {days[weekday]}s at {time}, {fmt(first.toISOString())} – {fmt(last.toISOString())}. Each can be edited or removed on its own.</>}
            </div>

            <div style={{ display: "flex", gap: 8 }}>
              <button onClick={create} disabled={saving}
                style={{ ...btn, flex: 1, background: C.navy, color: C.white, opacity: saving ? 0.6 : 1 }}>
                {saving ? (lang === "fi" ? "Luodaan…" : "Creating…") : (lang === "fi" ? "🏆 Luo kausi ja tapahtumat" : "🏆 Create season and events")}
              </button>
              <button onClick={() => { setCreating(false); setError(null); }} disabled={saving}
                style={{ ...btn, flex: 1, background: C.soft, color: C.text }}>
                {lang === "fi" ? "Peruuta" : "Cancel"}
              </button>
            </div>
          </div>
        ) : (
          <button onClick={() => setCreating(true)} disabled={!loaded}
            style={{ ...btn, width: "100%", marginTop: 12, background: C.navy, color: C.white }}>
            + {lang === "fi" ? "Luo uusi kausi" : "Create a season"}
          </button>
        )}

        {error && <div style={{ fontSize: 12, color: C.error, marginTop: 8 }}>{error}</div>}
      </div>
    </div>
  );
};

export default SeasonSettings;
